/* Trang thiết bị văn phòng: tình trạng, phân loại, khấu hao đường thẳng, bảo hành, tổng hợp theo đơn vị, số biên bản, nhập từ Excel. Port từ app cũ. */
import type { Grid } from './import';
import { nextCode } from './numbering';
import { rxDate, rxFindHead, rxGuess, rxNorm, rxNum, type RxField } from './recon';

/* ---------- tình trạng thiết bị ---------- */
export type EqStatus = 'use' | 'stock' | 'fix' | 'broken' | 'lost' | 'gone';
/** [mã, nhãn, màu của nhãn trạng thái trên giao diện] */
export const EQ_STATUS: [EqStatus, string, string][] = [
  ['use', 'Đang sử dụng', 'ok'], ['stock', 'Trong kho', 'wait'], ['fix', 'Đang sửa chữa', 'pause'], ['broken', 'Hỏng', 'off'], ['lost', 'Mất', 'off'], ['gone', 'Đã thanh lý', 'void'],
];
export const EQ_STATUS_MAP: Record<string, [EqStatus, string, string]> = Object.fromEntries(EQ_STATUS.map(x => [x[0], x]));
/** Số ngày tính là "sắp hết bảo hành". */
export const EQ_WARRANTY_SOON_DAYS = 60;

/* ---------- phân loại ---------- */
export interface EqCategory { id: string; name: string; /** ký hiệu: tiền tố mã thiết bị (LT → LT-0001) */ pre: string; /** số năm khấu hao (0 = không khấu hao) */ years: number; note: string }

export const EQ_CAT_DEFAULT: EqCategory[] = ([
  ['lt', 'Máy tính xách tay', 'LT', 3], ['pc', 'Máy tính để bàn', 'PC', 3], ['mh', 'Màn hình', 'MH', 3], ['mi', 'Máy in / scan / photo', 'MI', 3], ['dt', 'Điện thoại / máy tính bảng', 'DT', 3],
  ['mang', 'Thiết bị mạng', 'TBM', 3], ['bg', 'Bàn ghế, tủ kệ', 'BG', 5], ['dh', 'Điều hoà, thiết bị điện', 'DH', 5], ['khac', 'Thiết bị khác', 'TB', 3],
] as [string, string, string, number][]).map(([id, name, pre, years]) => ({ id, name, pre, years, note: '' }));

/** Danh mục đã lưu của đơn vị; chưa có (hoặc rỗng) thì dùng danh mục mặc định. */
export const eqCategories = (saved?: EqCategory[] | null): EqCategory[] => (Array.isArray(saved) && saved.length ? saved : EQ_CAT_DEFAULT);

/** Lỗi của danh mục phân loại trước khi lưu (rỗng = hợp lệ). Ký hiệu được viết hoa, bỏ khoảng trắng đầu/cuối. */
export function validateEqCategories(list: EqCategory[]): { error: string; list: EqCategory[] } {
  const out = list.map(c => ({ ...c, name: String(c.name || '').trim(), pre: String(c.pre || '').trim().toUpperCase(), years: Math.max(0, +c.years || 0), note: String(c.note || '').trim() }));
  if (!out.length) return { error: 'Cần ít nhất một loại thiết bị.', list: out };
  if (out.some(c => !c.name || !c.pre)) return { error: 'Nhập tên và ký hiệu cho mọi loại.', list: out };
  if (new Set(out.map(c => c.pre)).size !== out.length) return { error: 'Ký hiệu mã bị trùng.', list: out };
  return { error: '', list: out };
}

/* ---------- giá trị còn lại, bảo hành ---------- */
const MS_DAY = 864e5;

/** Giá trị còn lại theo khấu hao đường thẳng tính theo tháng; không có nguyên giá / số năm / ngày mua thì coi như chưa khấu hao. */
export function eqRemain(price: number, buyDate: string, years: number, now: Date = new Date()): number {
  const p = +price || 0, y = +years || 0;
  if (!p || !y || !buyDate) return p;
  const d0 = new Date(buyDate + 'T00:00:00');
  const months = (now.getFullYear() - d0.getFullYear()) * 12 + now.getMonth() - d0.getMonth();
  return Math.max(0, Math.round(p * (1 - Math.max(0, months) / (y * 12))));
}

/** 'none' = chưa nhập; 'expired' = đã hết; 'soon' = còn ≤ 60 ngày; 'ok' = còn lâu. `todayIso` dạng YYYY-MM-DD. */
export function eqWarrantyState(warranty: string | null | undefined, todayIso: string): 'none' | 'expired' | 'soon' | 'ok' {
  if (!warranty) return 'none';
  if (warranty < todayIso) return 'expired';
  const soon = new Date(new Date(todayIso + 'T00:00:00Z').getTime() + EQ_WARRANTY_SOON_DAYS * MS_DAY).toISOString().slice(0, 10);
  return warranty <= soon ? 'soon' : 'ok';
}

/* ---------- tổng hợp ---------- */
export interface EqLite { id?: string; code?: string; name?: string; cat: string; status: string; unit: string; price: number; buyDate: string; warranty?: string }

export interface EqKpi { total: number; inUse: number; stock: number; trouble: number; value: number; remain: number; warrantySoon: number }

/** Các ô tổng quan trên đầu danh sách: chỉ tính thiết bị chưa thanh lý. */
export function eqKpi(items: EqLite[], cats: EqCategory[], todayIso: string, now: Date = new Date()): EqKpi {
  const act = items.filter(x => x.status !== 'gone'), years = new Map(cats.map(c => [c.id, c.years]));
  return {
    total: act.length, inUse: act.filter(x => x.status === 'use').length, stock: act.filter(x => x.status === 'stock').length,
    trouble: act.filter(x => ['fix', 'broken', 'lost'].includes(x.status)).length,
    value: act.reduce((a, x) => a + (+x.price || 0), 0),
    remain: act.reduce((a, x) => a + eqRemain(x.price, x.buyDate, years.get(x.cat) ?? 0, now), 0),
    warrantySoon: act.filter(x => eqWarrantyState(x.warranty, todayIso) === 'soon').length,
  };
}

export interface EqUnitRow { unit: string; /** số thiết bị theo từng loại (cùng thứ tự `cats`) */ counts: number[]; total: number; value: number; remain: number }

/** Số lượng thiết bị theo đơn vị sử dụng × phân loại; thiết bị chưa phân bổ (không có đơn vị) xếp cuối. Không tính thiết bị đã thanh lý. */
export function eqUnitSummary(items: EqLite[], cats: EqCategory[], now: Date = new Date()): EqUnitRow[] {
  const act = items.filter(x => x.status !== 'gone'), years = new Map(cats.map(c => [c.id, c.years]));
  const units = [...new Set(act.map(x => x.unit || ''))].sort((a, b) => (!a ? 1 : !b ? -1 : a.localeCompare(b, 'vi')));
  return units.map(u => {
    const l = act.filter(x => (x.unit || '') === u);
    return {
      unit: u, counts: cats.map(c => l.filter(x => x.cat === c.id).length), total: l.length,
      value: l.reduce((a, x) => a + (+x.price || 0), 0), remain: l.reduce((a, x) => a + eqRemain(x.price, x.buyDate, years.get(x.cat) ?? 0, now), 0),
    };
  });
}

/* ---------- biên bản ---------- */
export type EqRecType = 'give' | 'back' | 'move';
export const ER_TYPES: Record<EqRecType, { label: string; title: string; pre: string; verb: string }> = {
  give: { label: 'Bàn giao (cấp phát)', title: 'BIÊN BẢN BÀN GIAO TRANG THIẾT BỊ', pre: 'BBBG', verb: 'bàn giao' },
  back: { label: 'Thu hồi', title: 'BIÊN BẢN THU HỒI TRANG THIẾT BỊ', pre: 'BBTH', verb: 'thu hồi' },
  move: { label: 'Điều chuyển', title: 'BIÊN BẢN ĐIỀU CHUYỂN TRANG THIẾT BỊ', pre: 'BBDC', verb: 'điều chuyển' },
};

/** Số biên bản kế tiếp trong năm của ngày lập: BBBG-2026-0001. */
export function erNextNo(type: EqRecType, dateIso: string, recs: { no?: string | null }[]): string {
  return nextCode(recs.map(r => ({ code: r.no })), `${ER_TYPES[type].pre}-${(dateIso || '').slice(0, 4) || new Date().getFullYear()}-`, 4);
}

/** Mã thiết bị kế tiếp theo ký hiệu loại (LT-0001). */
export const eqNextCode = (pre: string, list: { code?: string | null }[]): string => nextCode(list, (pre || 'TB') + '-', 4);

/** Thiết bị được chọn đưa vào biên bản: bàn giao chỉ từ kho; thu hồi / điều chuyển chỉ thiết bị đang sử dụng (có thể thu hẹp theo đơn vị bên giao). */
export function erCandidates<T extends { id: string; status: string; unit: string }>(type: EqRecType, items: T[], picked: Set<string>, fromUnit = '', hcns = ''): T[] {
  return items.filter(x => !picked.has(x.id) && x.status !== 'gone'
    && (type === 'give' ? x.status === 'stock' : x.status === 'use' && (!fromUnit || fromUnit === hcns || x.unit === fromUnit)));
}

/* ---------- nhập từ Excel ---------- */
export const EQ_IMPORT_FIELDS: RxField[] = [
  { k: 'code', l: 'Mã TB', t: 'text', syn: ['ma tb', 'ma thiet bi', 'ma tai san', '=ma'] },
  { k: 'name', l: 'Tên thiết bị', t: 'text', req: 1, syn: ['ten thiet bi', 'ten tai san', '=ten'] },
  { k: 'cat', l: 'Phân loại', t: 'text', syn: ['phan loai', 'loai thiet bi', '=loai', 'nhom'] },
  { k: 'brand', l: 'Hiệu / Model', t: 'text', syn: ['hieu', 'model', 'nhan hieu'] },
  { k: 'serial', l: 'Serial', t: 'text', syn: ['serial', 'so seri', 'service tag'] },
  { k: 'spec', l: 'Cấu hình', t: 'text', syn: ['cau hinh', 'thong so'] },
  { k: 'unitN', l: 'ĐVT', t: 'text', syn: ['dvt', 'don vi tinh'] },
  { k: 'buyDate', l: 'Ngày mua', t: 'date', syn: ['ngay mua', 'ngay dua vao su dung'] },
  { k: 'price', l: 'Nguyên giá', t: 'num', syn: ['nguyen gia', 'gia mua', 'gia tri'] },
  { k: 'warranty', l: 'Bảo hành đến', t: 'date', syn: ['bao hanh'] },
  { k: 'supplier', l: 'Nhà cung cấp', t: 'text', syn: ['nha cung cap', 'ncc'] },
  { k: 'unit', l: 'Đơn vị sử dụng', t: 'text', syn: ['don vi su dung', 'phong ban', 'bo phan'] },
  { k: 'holder', l: 'Người giữ (Mã NV hoặc họ tên)', t: 'text', syn: ['nguoi giu', 'nguoi su dung', 'ma nv'] },
  { k: 'status', l: 'Tình trạng', t: 'text', syn: ['tinh trang', 'trang thai'] },
  { k: 'location', l: 'Vị trí', t: 'text', syn: ['vi tri'] },
  { k: 'note', l: 'Ghi chú', t: 'text', syn: ['ghi chu'] },
];

export interface EqImportRow {
  /** dòng trong tệp (bắt đầu từ 1) */ row: number;
  /** có = cập nhật thiết bị đã có (trùng mã), không = thêm mới */ id?: string;
  code: string; name: string; cat: string; brand: string; serial: string; spec: string; unitN: string; buyDate: string | null; price: number; warranty: string | null;
  supplier: string; unit: string; holderId: string | null; holderName: string; status: EqStatus; location: string; note: string;
}
export interface EqImportPlan { rows: EqImportRow[]; errors: { row: number; message: string }[]; add: number; update: number; /** false = không thấy cột "Tên thiết bị" */ hasName: boolean }

export interface EqImportCtx {
  cats: EqCategory[];
  employees: { id: string; code: string; fullName: string; dept?: string }[];
  /** thiết bị đang có (để nhận biết mã trùng = cập nhật, và serial trùng) */
  existing: { id: string; code: string; serial: string }[];
}

/**
 * Đọc bảng tính thiết bị. Tiêu đề được dò tự động (không cần đúng thứ tự cột). Quy tắc như app cũ:
 *  - Mã trống → tự đánh theo ký hiệu loại; mã trùng thiết bị đã có → cập nhật dòng đó.
 *  - Phân loại ghi tên hoặc ký hiệu (không khớp → loại cuối danh mục).
 *  - Người giữ ghi Mã NV hoặc họ tên; đơn vị trống thì lấy phòng ban của người giữ.
 *  - Tình trạng trống: có đơn vị / người giữ → đang sử dụng, ngược lại → trong kho.
 * Thêm so với app cũ: dòng trùng mã hoặc serial với dòng khác trong tệp / thiết bị khác bị bỏ qua và báo lỗi (CSDL không cho trùng).
 */
export function planEquipmentImport(grid: Grid, ctx: EqImportCtx): EqImportPlan {
  // Bảng thiết bị luôn có MỘT dòng tiêu đề: không dùng cách ghép hai dòng tiêu đề của rxHeadAt (dòng dữ liệu đầu toàn chữ sẽ bị nhận nhầm là tiêu đề phụ và mất dòng).
  const head = rxFindHead(grid, EQ_IMPORT_FIELDS), hd = { labels: (grid[head] || []).map(c => (c === null || c === undefined ? '' : String(c))), start: head + 1 };
  const map = rxGuess(hd.labels, EQ_IMPORT_FIELDS);
  const plan: EqImportPlan = { rows: [], errors: [], add: 0, update: 0, hasName: map.name !== undefined && map.name >= 0 };
  if (!plan.hasName) return plan;
  const get = (r: Grid[number], k: string) => (map[k] !== undefined && map[k] >= 0 ? r[map[k]] : null);
  const s = (r: Grid[number], k: string) => String(get(r, k) ?? '').trim();
  const cats = ctx.cats, last = cats[cats.length - 1];
  const findCat = (v: string) => { const n = rxNorm(v); return cats.find(c => rxNorm(c.name) === n || rxNorm(c.pre) === n) || cats.find(c => n && rxNorm(c.name).includes(n)) || last; };
  const findEmp = (v: string) => { const n = rxNorm(v); return n ? ctx.employees.find(e => rxNorm(e.code) === n || rxNorm(e.fullName) === n) : undefined; };
  const findSt = (v: string): EqStatus | undefined => { const n = rxNorm(v); return (EQ_STATUS.find(x => rxNorm(x[1]) === n || x[0] === n) || [undefined])[0]; };

  const pool = ctx.existing.map(x => ({ ...x })), seenCode = new Set<string>(), seenSerial = new Map<string, string>(ctx.existing.filter(x => x.serial).map(x => [x.serial.toLowerCase(), x.code.toLowerCase()]));
  for (let i = hd.start; i < grid.length; i++) {
    const r = grid[i] || [], name = s(r, 'name');
    if (!name) continue;
    const cat = findCat(s(r, 'cat')), emp = findEmp(s(r, 'holder'));
    let code = s(r, 'code');
    const prev = code ? pool.find(x => x.code.toLowerCase() === code.toLowerCase()) : undefined;
    if (prev) code = prev.code;                                              // giữ đúng cách viết mã đã lưu (LT-0001, không phải lt-0001)
    if (!code) code = nextCode([...pool, ...plan.rows], cat.pre + '-', 4);
    if (seenCode.has(code.toLowerCase())) { plan.errors.push({ row: i + 1, message: `Mã ${code} bị lặp trong tệp.` }); continue; }
    const serial = s(r, 'serial'), owner = serial ? seenSerial.get(serial.toLowerCase()) : undefined;
    if (owner !== undefined && owner !== code.toLowerCase()) { plan.errors.push({ row: i + 1, message: `Serial ${serial} đã có ở thiết bị khác.` }); continue; }
    const unit = s(r, 'unit') || emp?.dept || '';
    const row: EqImportRow = {
      row: i + 1, ...(prev ? { id: prev.id } : {}), code, name, cat: cat.id, brand: s(r, 'brand'), serial, spec: s(r, 'spec'), unitN: s(r, 'unitN') || 'Cái',
      buyDate: rxDate(get(r, 'buyDate')) || null, price: Math.max(0, rxNum(get(r, 'price'))), warranty: rxDate(get(r, 'warranty')) || null, supplier: s(r, 'supplier'),
      unit, holderId: emp?.id ?? null, holderName: emp?.fullName || s(r, 'holder'), status: findSt(s(r, 'status')) || (unit || emp ? 'use' : 'stock'), location: s(r, 'location'), note: s(r, 'note'),
    };
    seenCode.add(code.toLowerCase());
    if (serial) seenSerial.set(serial.toLowerCase(), code.toLowerCase());
    plan.rows.push(row);
    if (prev) plan.update++; else plan.add++;
  }
  return plan;
}
